/* ==========================================================================
   Jamieson Vitamins — Purity Theme GTM / GA4 Data Layer
   --------------------------------------------------------------------------
   Purpose:
     Pushes standard GA4 ecommerce events to window.dataLayer so GTM tags
     can fire without any Liquid/JS living inside GTM itself.
 
   Events covered:
     - view_item            (PDP)
     - view_item_list        (collection / search grids)
     - select_item           (product card click)
     - add_to_cart            (fetch + XHR hook on /cart/add, quick add, PDP)
     - remove_from_cart       (cart drawer / cart page line removal)
     - view_cart              (cart drawer open + /cart page load)
     - begin_checkout          (checkout button click)
     - search / view_search_results
 
   Liquid hooks this script expects (see companion snippet
   `snippets/dl-json.liquid` referenced at the bottom of this file):
     #dl-product          -> single product object   (product templates)
     #dl-collection       -> { id, title, items: [...] } (collection/search templates)
     #dl-cart             -> { items: [...] }         (optional, cart page — falls back to /cart.js)
 
   Product card markup contract (any card that should report select_item /
   view_item_list correctly should carry these data attributes on the
   outermost clickable wrapper):
     data-dl-item              (JSON string, single item — see buildItem() shape)
     data-dl-list-id           (e.g. "collection-immune-support")
     data-dl-list-name         (e.g. "Immune Support")
     data-dl-index             (0-based position in the grid)
 
   Load this file once, theme-wide, ideally deferred (e.g. via
   `{{ 'purity-gtm-datalayer.js' | asset_url | script_tag: defer: true }}`
   in theme.liquid, after the dataLayer is first declared by GTM's own
   snippet).
   ========================================================================== */
 
(function () {
  window.dataLayer = window.dataLayer || [];
 
  /* ------------------------------------------------------------------ */
  /* Shared utilities                                                    */
  /* ------------------------------------------------------------------ */
 
  const toNum = (x) => {
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
  };
 
  const safeJSONParse = (text, fallback) => {
    try {
      return JSON.parse(text);
    } catch (e) {
      return fallback;
    }
  };
 
  const readJSONElement = (id) => {
    const el = document.getElementById(id);
    if (!el) return null;
    return safeJSONParse(el.textContent || "", null);
  };
 
  const getCurrency = () =>
    window.Shopify?.currency?.active ||
    window.Shopify?.currency?.currency ||
    document.documentElement.getAttribute("data-shopify-currency") ||
    "USD";
 
  const getPageType = () => {
    const path = window.location.pathname || "";
    if (document.getElementById("dl-product")) return "product";
    if (document.getElementById("dl-collection")) return "collection";
    if (path.startsWith("/products/")) return "product";
    if (path.startsWith("/collections/")) return "collection";
    if (path.startsWith("/search")) return "search";
    if (path === "/cart" || path.startsWith("/cart/")) return "cart";
    if (path.startsWith("/checkout")) return "checkout";
    return "other";
  };
 
  /**
   * Generic short-window dedupe so double-bound listeners, theme app
   * duplication, or rapid double clicks don't double-fire an event.
   */
  const recentEvents = new Map(); // key -> timestamp
  const isDuplicateEvent = (key, windowMs = 1200) => {
    const now = Date.now();
    for (const [k, ts] of recentEvents.entries()) {
      if (now - ts > windowMs) recentEvents.delete(k);
    }
    if (recentEvents.has(key)) return true;
    recentEvents.set(key, now);
    return false;
  };
 
  /**
   * Normalizes a variety of possible source shapes (Shopify AJAX API
   * cart line, product JSON, card dataset) into a single GA4 item object.
   */
  const buildItem = (source, opts = {}) => {
    const quantity = toNum(source.quantity ?? 1);
    const priceCents =
      source.final_price ?? source.line_price ?? source.price_cents ?? null;
    const price =
      priceCents != null ? toNum(priceCents) / 100 : toNum(source.price);
 
    const item = {
      item_id: String(source.variant_id || source.id || source.product_id || ""),
      item_name: source.product_title || source.title || source.name || "",
      item_brand: source.vendor || source.brand || "",
      item_category: source.product_type || source.category || "",
      item_variant: source.variant_title || source.variant || "",
      price,
      quantity
    };
 
    if (opts.listId) item.item_list_id = opts.listId;
    if (opts.listName) item.item_list_name = opts.listName;
    if (opts.index != null) item.index = toNum(opts.index);
 
    return item;
  };
 
  const pushEcommerce = (event, { items, value, extra = {} } = {}) => {
    window.dataLayer.push({ ecommerce: null }); // GA4 recommended: clear previous ecommerce object
    window.dataLayer.push({
      event,
      page_type: getPageType(),
      ...extra,
      ecommerce: {
        currency: getCurrency(),
        value: value != null ? toNum(value) : undefined,
        items: items || []
      }
    });
  };
 
  /* ------------------------------------------------------------------ */
  /* view_item — Product page                                            */
  /* ------------------------------------------------------------------ */
 
  (function initViewItem() {
    const p = readJSONElement("dl-product");
    if (!p || !p.variant_id) return;
 
    pushEcommerce("view_item", {
      value: toNum(p.price),
      items: [buildItem(p)]
    });
  })();
 
  /* ------------------------------------------------------------------ */
  /* view_item_list — Collection / search grid                           */
  /* ------------------------------------------------------------------ */
 
  (function initViewItemList() {
    const c = readJSONElement("dl-collection");
    if (!c || !Array.isArray(c.items) || !c.items.length) return;
 
    const items = c.items.map((item, index) =>
      buildItem(item, { listId: c.id ? `collection-${c.id}` : undefined, listName: c.title, index })
    );
 
    window.dataLayer.push({
      event: "view_item_list",
      page_type: getPageType(),
      item_list_id: c.id ? `collection-${c.id}` : undefined,
      item_list_name: c.title || "",
      ecommerce: {
        currency: getCurrency(),
        items
      }
    });
  })();
 
  /* ------------------------------------------------------------------ */
  /* select_item — Click on a product card                               */
  /* ------------------------------------------------------------------ */
 
  (function initSelectItem() {
    document.addEventListener("click", (e) => {
      const card = e.target.closest("[data-dl-item]");
      if (!card) return;
 
      const raw = safeJSONParse(card.getAttribute("data-dl-item"), null);
      if (!raw) return;
 
      const item = buildItem(raw, {
        listId: card.getAttribute("data-dl-list-id") || undefined,
        listName: card.getAttribute("data-dl-list-name") || undefined,
        index: card.getAttribute("data-dl-index")
      });
 
      window.dataLayer.push({
        event: "select_item",
        page_type: getPageType(),
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
        ecommerce: {
          currency: getCurrency(),
          items: [item]
        }
      });
    });
  })();
 
  /* ------------------------------------------------------------------ */
  /* add_to_cart — fetch + XHR hook on /cart/add(.js)                     */
  /* ------------------------------------------------------------------ */
 
  const isAddUrl = (url) => {
    if (!url) return false;
    const u = String(url);
    return (
      /\/cart\/add\.js(\?|$)/.test(u) ||
      /\/cart\/add(\?|$)/.test(u) ||
      /add\.js(\?|$)/.test(u)
    );
  };
 
  const pushAddToCart = (added, source) => {
    const item = buildItem(added);
    const key = `atc:${item.item_id}:${item.quantity}`;
    if (isDuplicateEvent(key)) return;
 
    pushEcommerce("add_to_cart", {
      value: item.price * item.quantity,
      items: [item],
      extra: { atc_source: source }
    });
  };
 
  (function initAddToCartFetchHook() {
    const originalFetch = window.fetch;
    if (typeof originalFetch !== "function") return;
 
    window.fetch = async function (...args) {
      const res = await originalFetch.apply(this, args);
 
      try {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
        if (!isAddUrl(url) || !res.ok) return res;
 
        const cloned = res.clone();
        const text = await cloned.text();
        if (!text) return res;
 
        const added = safeJSONParse(text, null);
        if (added && (added.id || added.variant_id)) pushAddToCart(added, "fetch");
      } catch (e) {
        /* noop — never let tracking break the cart request */
      }
 
      return res;
    };
  })();
 
  (function initAddToCartXhrHook() {
    const XHR = window.XMLHttpRequest;
    if (typeof XHR !== "function") return;
 
    const originalOpen = XHR.prototype.open;
    const originalSend = XHR.prototype.send;
 
    XHR.prototype.open = function (method, url, ...rest) {
      this.__dl_url = url;
      return originalOpen.call(this, method, url, ...rest);
    };
 
    XHR.prototype.send = function (body) {
      this.addEventListener("load", function () {
        try {
          const url = this.__dl_url || "";
          if (!isAddUrl(url) || this.status < 200 || this.status >= 300) return;
 
          const text = this.responseText || "";
          if (!text) return;
 
          const added = safeJSONParse(text, null);
          if (added && (added.id || added.variant_id)) pushAddToCart(added, "xhr");
        } catch (e) {
          /* noop */
        }
      });
 
      return originalSend.call(this, body);
    };
  })();
 
  /**
   * Quick-add / static buy buttons that don't go through /cart/add (e.g.
   * a custom button that just calls a cart API wrapper) can opt in by
   * dispatching this event instead of relying on the network hooks:
   *
   *   document.dispatchEvent(new CustomEvent('dl:add_to_cart', {
   *     detail: { item: {...}, source: 'quick_add' }
   *   }));
   */
  document.addEventListener("dl:add_to_cart", (e) => {
    const { item, source } = e.detail || {};
    if (item) pushAddToCart(item, source || "custom");
  });
 
  /* ------------------------------------------------------------------ */
  /* remove_from_cart — cart drawer / cart page line removal              */
  /* ------------------------------------------------------------------ */
 
  const isChangeUrl = (url) => /\/cart\/change\.js(\?|$)/.test(String(url || ""));
 
  const pushRemoveFromCart = (line, quantityRemoved) => {
    if (!line || quantityRemoved <= 0) return;
    const item = buildItem({ ...line, quantity: quantityRemoved });
    const key = `rfc:${item.item_id}:${quantityRemoved}:${Date.now()}`;
    if (isDuplicateEvent(key, 500)) return;
 
    pushEcommerce("remove_from_cart", {
      value: item.price * item.quantity,
      items: [item]
    });
  };
 
  /**
   * /cart/change.js only returns the updated cart, not the delta, so the
   * theme's cart-item component should dispatch this event with the
   * before/after quantity whenever a line is decremented or removed:
   *
   *   document.dispatchEvent(new CustomEvent('dl:remove_from_cart', {
   *     detail: { line: {...}, quantityRemoved: 1 }
   *   }));
   */
  document.addEventListener("dl:remove_from_cart", (e) => {
    const { line, quantityRemoved } = e.detail || {};
    pushRemoveFromCart(line, toNum(quantityRemoved));
  });
 
  /* ------------------------------------------------------------------ */
  /* view_cart — cart drawer open + /cart page load                      */
  /* ------------------------------------------------------------------ */
 
  const pushViewCart = (cart) => {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;
    if (isDuplicateEvent("view_cart", 800)) return;
 
    const items = cart.items.map((line) => buildItem(line));
    pushEcommerce("view_cart", {
      value: toNum(cart.total_price) / 100,
      items
    });
  };
 
  const fetchCartAndPushViewCart = () => {
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => cart && pushViewCart(cart))
      .catch(() => {});
  };
 
  (function initViewCart() {
    // Cart page render
    const cartJson = readJSONElement("dl-cart");
    if (cartJson) {
      pushViewCart(cartJson);
    } else if (getPageType() === "cart") {
      fetchCartAndPushViewCart();
    }
 
    // Cart drawer open — theme dispatches this when the drawer becomes active
    // (see cart-drawer.js: `document.dispatchEvent(new CustomEvent('dl:view_cart'))`)
    document.addEventListener("dl:view_cart", fetchCartAndPushViewCart);
  })();
 

 
  /* ------------------------------------------------------------------ */
  /* search + view_search_results                                        */
  /* ------------------------------------------------------------------ */
 
  document.addEventListener("submit", (e) => {
    const form = e.target;
    if (!form || form.action?.includes("/search") !== true) return;
 
    const fd = new FormData(form);
    const q = (fd.get("q") || "").toString().trim();
    if (!q) return;
 
    window.dataLayer.push({
      event: "search",
      page_type: getPageType(),
      search_term: q
    });
  });
 
  (function initViewSearchResults() {
    if (getPageType() !== "search") return;
    const results = readJSONElement("dl-search-results");
    if (!results) return;
 
    const params = new URLSearchParams(window.location.search);
    const searchTerm = params.get("q") || results.term || "";
 
    window.dataLayer.push({
      event: "view_search_results",
      page_type: "search",
      search_term: searchTerm,
      ecommerce: {
        currency: getCurrency(),
        items: (results.items || []).map((item, index) => buildItem(item, { index }))
      }
    });
  })();
 
  /* ------------------------------------------------------------------ */
  /* faq_toggle — accordion expand / collapse                            */
  /* ------------------------------------------------------------------ */
 
  /**
   * The <details> "toggle" event does NOT bubble per the HTML spec, so a
   * normal document-level listener would miss it. Listening in the
   * capture phase catches it regardless of how many accordions are on
   * the page, without needing a listener bound to each one individually.
   */
  document.addEventListener(
    "toggle",
    (e) => {
      const details = e.target;
      if (!details || details.tagName !== "DETAILS") return;
      if (!details.hasAttribute("data-dl-faq-question")) return;
 
      const question = details.getAttribute("data-dl-faq-question") || "";
      const group = details.getAttribute("data-dl-faq-group") || "";
      const action = details.open ? "expand" : "collapse";
 
      window.dataLayer.push({
        event: "faq_toggle",
        page_type: getPageType(),
        faq_action: action,
        faq_question: question,
        faq_group: group
      });
    },
    true // capture — required since "toggle" doesn't bubble
  );
 
  /* ------------------------------------------------------------------ */
  /* collection_view — kept for backward compatibility with legacy GTM   */
  /* triggers already built against this event name.                     */
  /* ------------------------------------------------------------------ */
 
  (function initLegacyCollectionView() {
    const c = readJSONElement("dl-collection");
    if (!c || !c.id) return;
 
    window.dataLayer.push({
      event: "collection_view",
      page_type: "collection",
      collection: { id: String(c.id), title: c.title || "" }
    });
  })();
})();